import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Typography, 
  Input, 
  Select, 
  Form, 
  Tag, 
  Space, 
  Popconfirm, 
  message, 
  Tabs, 
  List, 
  Avatar, 
  Row, 
  Col,
  Badge,
  Divider,
  Progress,
  DatePicker,
  InputNumber,
  Tooltip
} from 'antd';
import { 
  BookOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CalendarOutlined, 
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ArrowLeftOutlined,
  TrophyOutlined,
  BulbOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { studyConfig } from '../config/study';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';
import dayjs from 'dayjs';

// 配置axios基础URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Study = () => {
  const navigate = useNavigate();
  const { initialize } = useAuthStore();
  const [studies, setStudies] = useState([]);
  const [studyProgress, setStudyProgress] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [currentStudy, setCurrentStudy] = useState(null);
  const [activeTab, setActiveTab] = useState('studies');
  const [form] = Form.useForm();
  const [progressForm] = Form.useForm();

  // 加载学习数据
  const fetchStudies = async () => {
    setLoading(true);
    try {
      const response = await axios.get(studyConfig.apiEndpoints.studies);
      if (response.data.success) {
        setStudies(response.data.studies || []);
      } else {
        message.error(response.data.message || '获取学习数据失败');
      }
    } catch (error) {
      console.error('获取学习数据失败:', error);
      message.error('获取学习数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载学习进度
  const fetchStudyProgress = async (studyId) => {
    try {
      const response = await axios.get(`${studyConfig.apiEndpoints.progress}/${studyId}`);
      if (response.data.success) {
        setStudyProgress(prev => ({
          ...prev,
          [studyId]: response.data.progress || []
        }));
      }
    } catch (error) {
      console.error('获取学习进度失败:', error);
    }
  };

  // 加载所有学习进度
  const fetchAllProgress = async () => {
    for (const study of studies) {
      await fetchStudyProgress(study.id);
    }
  };

  // 添加学习内容
  const handleAdd = () => {
    setCurrentStudy(null);
    form.resetFields();
    setShowAddModal(true);
  };

  // 编辑学习内容
  const handleEdit = (study) => {
    setCurrentStudy(study);
    form.setFieldsValue({
      ...study,
      start_date: study.start_date ? dayjs(study.start_date) : null,
      target_date: study.target_date ? dayjs(study.target_date) : null
    });
    setShowEditModal(true);
  };

  // 删除学习内容
  const handleDelete = async (studyId) => {
    try {
      const response = await axios.delete(`${studyConfig.apiEndpoints.delete}/${studyId}`);
      if (response.data.success) {
        message.success('删除成功');
        fetchStudies();
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除学习内容失败:', error);
      message.error('删除失败');
    }
  };

  // 保存学习内容
  const handleSaveStudy = async (values) => {
    try {
      let response;
      const requestData = {
        ...values,
        start_date: values.start_date ? (dayjs.isDayjs(values.start_date) ? values.start_date.format('YYYY-MM-DD') : values.start_date) : null,
        target_date: values.target_date ? (dayjs.isDayjs(values.target_date) ? values.target_date.format('YYYY-MM-DD') : values.target_date) : null
      };

      if (currentStudy) {
        response = await axios.put(`${studyConfig.apiEndpoints.update}/${currentStudy.id}`, requestData);
      } else {
        response = await axios.post(studyConfig.apiEndpoints.create, requestData);
      }

      if (response.data.success) {
        message.success(currentStudy ? '更新成功' : '添加成功');
        setShowAddModal(false);
        setShowEditModal(false);
        fetchStudies();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存学习内容失败:', error);
      message.error('操作失败');
    }
  };

  // 查看学习进度
  const handleViewProgress = (study) => {
    setCurrentStudy(study);
    setShowProgressModal(true);
  };

  // 添加学习进度
  const handleAddProgress = (study) => {
    setCurrentStudy(study);
    progressForm.resetFields();
    setShowProgressModal(true);
  };

  // 保存学习进度
  const handleSaveProgress = async (values) => {
    try {
      const response = await axios.post(studyConfig.apiEndpoints.addProgress, {
        study_id: currentStudy.id,
        ...values
      });

      if (response.data.success) {
        message.success('进度记录添加成功');
        progressForm.resetFields();
        fetchStudyProgress(currentStudy.id);
      } else {
        message.error(response.data.message || '添加失败');
      }
    } catch (error) {
      console.error('添加学习进度失败:', error);
      message.error('添加失败');
    }
  };

  // 删除学习进度
  const handleDeleteProgress = async (progressId) => {
    try {
      const response = await axios.delete(`${studyConfig.apiEndpoints.deleteProgress}/${progressId}`);
      if (response.data.success) {
        message.success('进度记录删除成功');
        fetchStudyProgress(currentStudy.id);
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除学习进度失败:', error);
      message.error('删除失败');
    }
  };

  // 计算学习进度百分比
  const calculateProgress = (study) => {
    const progress = studyProgress[study.id] || [];
    if (progress.length === 0) return 0;
    
    const totalProgress = progress.reduce((sum, p) => sum + (p.progress_percentage || 0), 0);
    return Math.min(100, Math.round(totalProgress / progress.length));
  };

  // 获取状态图标
  const getStatusIcon = (status) => {
    switch (status) {
      case 'not_started':
        return <ClockCircleOutlined style={{ color: '#FF4D4F' }} />;
      case 'in_progress':
        return <PlayCircleOutlined style={{ color: '#1890FF' }} />;
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52C41A' }} />;
      case 'paused':
        return <PauseCircleOutlined style={{ color: '#FAAD14' }} />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  // 统计信息
  const inProgressCount = studies.filter(s => (s.status || 'not_started') === 'in_progress').length;
  const completedCount = studies.filter(s => s.status === 'completed').length;
  const notStartedCount = studies.filter(s => (s.status || 'not_started') === 'not_started').length;

  // 组件加载时获取数据
  useEffect(() => {
    initialize();
    fetchStudies();
  }, [initialize]);

  // 当学习数据更新时，加载学习进度
  useEffect(() => {
    if (studies.length > 0) {
      fetchAllProgress();
    }
  }, [studies]);

  return (
    <div className="mod-page">
      {/* 头部 */}
      <div className="mod-header">
        <div>
          <div className="mod-header-title">
            <BookOutlined style={{ marginRight: '10px', color: '#6366F1' }} />
            {studyConfig.name}
          </div>
          <div className="mod-header-subtitle">
            {studyConfig.description} —— 定目标、做计划、勤复习、常总结，让学习有迹可循
          </div>
        </div>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
          >
            返回
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            style={{ backgroundColor: '#6366F1', borderColor: '#6366F1' }}
          >
            添加学习内容
          </Button>
        </Space>
      </div>

      {/* 统计卡 */}
      {studies.length > 0 && (
        <div className="mod-stats">
          <div className="mod-stat-card">
            <div className="mod-stat-icon"><BookOutlined /></div>
            <div className="mod-stat-value">{studies.length}</div>
            <div className="mod-stat-label">学习内容</div>
          </div>
          <div className="mod-stat-card">
            <div className="mod-stat-icon" style={{ background: 'rgba(24,144,255,0.12)', color: '#1890FF' }}><PlayCircleOutlined /></div>
            <div className="mod-stat-value">{inProgressCount}</div>
            <div className="mod-stat-label">进行中</div>
          </div>
          <div className="mod-stat-card">
            <div className="mod-stat-icon" style={{ background: 'rgba(82,196,26,0.12)', color: '#52C41A' }}><CheckCircleOutlined /></div>
            <div className="mod-stat-value">{completedCount}</div>
            <div className="mod-stat-label">已完成</div>
          </div>
          <div className="mod-stat-card">
            <div className="mod-stat-icon" style={{ background: 'rgba(255,77,79,0.12)', color: '#FF4D4F' }}><ClockCircleOutlined /></div>
            <div className="mod-stat-value">{notStartedCount}</div>
            <div className="mod-stat-label">未开始</div>
          </div>
        </div>
      )}

      {/* 主要内容 */}
      <div style={{ padding: '0 6px' }}>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={[
            {
              key: 'studies',
              label: '学习内容',
              children: (
                <Card>
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <Text>加载中...</Text>
                    </div>
                  ) : studies.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <Text style={{ color: '#666' }}>暂无学习内容，点击"添加学习内容"按钮开始添加</Text>
                    </div>
                  ) : (
                    <Row gutter={[16, 16]}>
                      {studies.map((study) => {
                        const progress = studyProgress[study.id] || [];
                        const progressPercentage = calculateProgress(study);
                        // 直接使用后端返回的状态
                        const studyStatus = study.status || 'not_started';
                        const status = studyConfig.studyStatus.find(s => s.value === studyStatus);
                        
                        return (
                          <Col xs={24} sm={12} md={8} lg={6} key={study.id}>
                            <Card
                              size="small"
                              hoverable
                              style={{
                                borderRadius: '14px',
                                boxShadow: '0 4px 16px rgba(99, 102, 241, 0.08)',
                                background: '#fff',
                                border: '1px solid #f0f0f5',
                                borderTop: `3px solid ${status?.color || '#6366F1'}`,
                                transition: 'all 0.3s ease',
                                height: '100%'
                              }}
                              bodyStyle={{ padding: '14px' }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.boxShadow = '0 10px 28px rgba(99, 102, 241, 0.16)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 16px rgba(99, 102, 241, 0.08)';
                              }}
                            >
                              {/* 学习内容标题和状态 */}
                              <div style={{ marginBottom: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                  <Avatar 
                                    icon={<BookOutlined />} 
                                    style={{ 
                                      backgroundColor: status?.color || '#999',
                                      marginRight: '8px'
                                    }} 
                                  />
                                  <Text strong style={{ fontSize: '16px' }}>{study.title}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Space>
                                    {getStatusIcon(studyStatus)}
                                    <Tag color={status?.color}>{status?.label}</Tag>
                                  </Space>
                                  <Tag color="blue">{study.study_type || '学习'}</Tag>
                                </div>
                              </div>
                              
                              <Divider style={{ margin: '8px 0' }} />
                              
                              {/* 学习目标和时间 */}
                              <div style={{ marginBottom: '12px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                  <Text type="secondary">学习目标：</Text>
                                  <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{study.goal}</Text>
                                </div>
                                <div style={{ marginBottom: '8px' }}>
                                  <Text type="secondary">学习方法：</Text>
                                  <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{study.method}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <div>
                                    <Text type="secondary">开始：</Text>
                                    <Text>{study.start_date || '未设置'}</Text>
                                  </div>
                                  <div>
                                    <Text type="secondary">目标：</Text>
                                    <Text>{study.target_date || '未设置'}</Text>
                                  </div>
                                </div>
                              </div>
                              
                              {/* 学习进度 */}
                              <div style={{ marginBottom: '12px' }}>
                                <div style={{ marginBottom: '4px' }}>
                                  <Text type="secondary">学习进度</Text>
                                </div>
                                <Progress 
                                  percent={progressPercentage} 
                                  size="small" 
                                  strokeColor={status?.color}
                                  format={(percent) => `${percent}%`}
                                />
                              </div>
                              
                              {/* 操作按钮 */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Space>
                                  <Badge count={progress.length} size="small">
                                    <Tooltip title="进度记录">
                                      <Button 
                                        type="link" 
                                        size="small"
                                        icon={<TrophyOutlined />}
                                        onClick={() => handleViewProgress(study)}
                                      />
                                    </Tooltip>
                                  </Badge>
                                </Space>
                                <Space size="small">
                                  <Tooltip title="编辑">
                                    <Button 
                                      type="link" 
                                      size="small"
                                      icon={<EditOutlined />}
                                      onClick={() => handleEdit(study)}
                                    />
                                  </Tooltip>
                                  <Popconfirm
                                    title="确定要删除这个学习内容吗？"
                                    onConfirm={() => handleDelete(study.id)}
                                    okText="确定"
                                    cancelText="取消"
                                  >
                                    <Button 
                                      type="link" 
                                      size="small"
                                      danger 
                                      icon={<DeleteOutlined />}
                                    >
                                      删除
                                    </Button>
                                  </Popconfirm>
                                </Space>
                              </div>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </Card>
              )
            },
            {
              key: 'keys',
              label: '四大关键',
              children: (
                <Card title={studyConfig.studyManagementKeys.title}>
                  {studyConfig.studyManagementKeys.sections.map((section, index) => (
                    <div key={index} style={{ marginBottom: '24px' }}>
                      <Title level={4} style={{ color: studyConfig.bgColor }}>
                        {section.title}
                      </Title>
                      <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
                        {section.subtitle}
                      </Text>
                      <ul style={{ paddingLeft: '20px' }}>
                        {section.items.map((item, itemIndex) => (
                          <li key={itemIndex} style={{ marginBottom: '8px' }}>
                            <Text>{item}</Text>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  <Divider />
                  <div style={{ textAlign: 'center', padding: '16px' }}>
                    <Text strong style={{ fontSize: '16px', color: studyConfig.bgColor }}>
                      {studyConfig.studyManagementKeys.summary}
                    </Text>
                  </div>
                </Card>
              )
            },
            {
              key: 'tips',
              label: '学习技巧',
              children: (
                <Card title={studyConfig.studyTips.title}>
                  {studyConfig.studyTips.sections.map((section, index) => (
                    <div key={index} style={{ marginBottom: '24px' }}>
                      <Title level={4} style={{ color: studyConfig.bgColor }}>
                        {section.title}
                      </Title>
                      <ul style={{ paddingLeft: '20px' }}>
                        {section.items.map((item, itemIndex) => (
                          <li key={itemIndex} style={{ marginBottom: '8px' }}>
                            <Text>{item}</Text>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </Card>
              )
            },
            {
              key: 'tools',
              label: '学习工具',
              children: (
                <Card title={studyConfig.studyTools.title}>
                  {studyConfig.studyTools.sections.map((section, index) => (
                    <div key={index} style={{ marginBottom: '24px' }}>
                      <Title level={4} style={{ color: studyConfig.bgColor }}>
                        {section.title}
                      </Title>
                      <ul style={{ paddingLeft: '20px' }}>
                        {section.items.map((item, itemIndex) => (
                          <li key={itemIndex} style={{ marginBottom: '8px' }}>
                            <Text>{item}</Text>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </Card>
              )
            }
          ]}
        />
      </div>

      {/* 添加/编辑学习内容弹窗 */}
      {(showAddModal || showEditModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                {currentStudy ? '编辑学习内容' : '添加学习内容'}
              </h3>
              <Button
                type="text"
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                }}
                style={{ fontSize: '18px' }}
              >
                ×
              </Button>
            </div>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveStudy}
        >
          <Form.Item
            name="title"
            label="学习内容"
            rules={[{ required: true, message: '请输入学习内容' }]}
          >
            <Input placeholder="请输入学习内容标题" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="study_type"
                label="学习类型"
                rules={[{ required: true, message: '请选择学习类型' }]}
              >
                <Select placeholder="请选择学习类型">
                  {studyConfig.studyTypes.map(type => (
                    <Option key={type} value={type}>{type}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="method"
                label="学习方法"
                rules={[{ required: true, message: '请选择学习方法' }]}
              >
                <Select placeholder="请选择学习方法">
                  {studyConfig.studyMethods.map(method => (
                    <Option key={method} value={method}>{method}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="goal"
            label="学习目标"
            rules={[{ required: true, message: '请输入学习目标' }]}
          >
            <TextArea rows={3} placeholder="请输入学习目标" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="start_date"
                label="开始时间"
              >
                <DatePicker style={{ width: '100%' }} placeholder="选择开始时间" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="target_date"
                label="目标完成时间"
              >
                <DatePicker style={{ width: '100%' }} placeholder="选择目标时间" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="status"
            label="学习状态"
            rules={[{ required: true, message: '请选择学习状态' }]}
          >
            <Select placeholder="请选择学习状态">
              {studyConfig.studyStatus.map(status => (
                <Option key={status.value} value={status.value}>
                  <Tag color={status.color}>{status.label}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea rows={3} placeholder="请输入备注信息" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {currentStudy ? '更新' : '添加'}
              </Button>
              <Button onClick={() => {
                setShowAddModal(false);
                setShowEditModal(false);
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
            
          </div>
        </div>
      )}

      {/* 学习进度弹窗 */}
      {showProgressModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                {currentStudy?.title || ''} - 学习进度
              </h3>
              <Button
                type="text"
                onClick={() => setShowProgressModal(false)}
                style={{ fontSize: '18px' }}
              >
                ×
              </Button>
            </div>
        <div style={{ marginBottom: '16px' }}>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => progressForm.resetFields()}
            style={{ backgroundColor: studyConfig.bgColor, borderColor: studyConfig.bgColor }}
          >
            添加进度
          </Button>
        </div>
        
        <Form
          form={progressForm}
          layout="vertical"
          onFinish={handleSaveProgress}
        >
          <Form.Item
            name="content"
            label="学习内容"
            rules={[{ required: true, message: '请输入学习内容' }]}
          >
            <TextArea rows={3} placeholder="请输入今天学习的内容" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="progress_percentage"
                label="进度百分比"
                rules={[{ required: true, message: '请输入进度百分比' }]}
              >
                <InputNumber 
                  min={0} 
                  max={100} 
                  placeholder="0-100" 
                  style={{ width: '100%' }}
                  addonAfter="%"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="study_time"
                label="学习时长(分钟)"
              >
                <InputNumber 
                  min={0} 
                  placeholder="学习时长" 
                  style={{ width: '100%' }}
                  addonAfter="分钟"
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="notes"
            label="学习笔记"
          >
            <TextArea rows={4} placeholder="记录学习心得、遇到的问题等" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                保存进度
              </Button>
              <Button onClick={() => progressForm.resetFields()}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
        
        <div style={{ marginTop: '24px' }}>
          <Title level={5}>历史进度</Title>
          <List
            dataSource={currentStudy ? (studyProgress[currentStudy.id] || []) : []}
            renderItem={(progress) => (
              <List.Item
                actions={[
                  <Popconfirm
                    title="确定要删除这条进度记录吗？"
                    onConfirm={() => handleDeleteProgress(progress.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button type="link" danger size="small">
                      删除
                    </Button>
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <CalendarOutlined />
                      <Text>{new Date(progress.created_at).toLocaleString('zh-CN')}</Text>
                      <Tag color="blue">{progress.progress_percentage}%</Tag>
                      {progress.study_time && <Tag color="green">{progress.study_time}分钟</Tag>}
                    </Space>
                  }
                  description={
                    <div>
                      <div style={{ marginBottom: '8px' }}>
                        <Text strong>学习内容：</Text>
                        <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{progress.content}</Text>
                      </div>
                      {progress.notes && (
                        <div>
                          <Text strong>学习笔记：</Text>
                          <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{progress.notes}</Text>
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Study;
